import { FC, ReactNode } from "react";
import { Box, Card, Container, Grid, GridItem, VBox } from "@/components-v2/Box";
import { isMobileView } from "@/utils/screen";
import { isTMessage, TFunction, TMessage } from "@/i18n/tt";
import { useMouseEventHandler, useTranslation } from "@/stores/RootStoreContext";
import { IconName } from "@/components-v2/iconMapping";
import { Icon } from "@/components-v2/Icon";
import { Tag, TagProps } from "@/components-v2/Tag";
import { Button, LinkButton } from "@/components-v2/Button";
import { observer } from "mobx-react";
import { Markdown } from "@/text/Markdown";
import { InfoTag, InfoTagDetails, InfoTagProps } from "@/components-v2/InfoTag";
import { when } from "@/utils/functions";
import { DCEvent } from "@/events/eventsDomain";
import { StringFieldModel } from "@/common/commonForms";
import { DcompanyLogoImg } from "@/components-v2/Logo";
import styles from "@/Login/LoginComponents.module.css";
import { Alert } from "@/components-v2/Alert";
import { MTextInput } from "@/components-v2/TextInput";
import { isEnter } from "@/utils/keyboardEvents";
import { CommentsView } from "@/comments/commentsComponents";
import { CommentListProps } from "@/comments/commentsDomain";

const isMarkdown = (o: unknown): o is { type: "markdown"; text: string } =>
  typeof o === "object" && o !== null && "type" in o && o.type === "markdown";

const renderMessageOrMarkdown = (t: TFunction, o: unknown): ReactNode =>
  isMarkdown(o) ? <Markdown value={o.text} /> : isTMessage(o) ? t(o) : null;

export type AgendaItemCardProps = {
  children?: React.ReactNode;
  description?: TMessage | { type: "markdown"; text: string };
  id: string;
  infoTags?: InfoTagProps[];
  openLabel?: TMessage;
  openVariant?: "primary" | "link";
  onClose?: (() => void) | DCEvent[];
  onEdit?: (() => void) | DCEvent[];
  onOpen?: (() => void) | DCEvent[];
  status?: TagProps;
  subtitleIcon?: IconName;
  subtitleText?: TMessage | { type: "markdown"; text: string };
  title: TMessage;
  comments?: CommentListProps;
};

export const AgendaItemSubTitle: FC<AgendaItemCardProps> = observer((props) => {
  const { t } = useTranslation();
  return props.subtitleText || props.status ? (
    <Box alignItems={"center"}>
      {props.status && <Tag {...props.status} />}
      {props.subtitleIcon && <Icon name={props.subtitleIcon} />}
      {renderMessageOrMarkdown(t, props.subtitleText)}
    </Box>
  ) : null;
});

export const AgendaItemHeader: FC<AgendaItemCardProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <h4>{t(props.title)}</h4>
      <AgendaItemSubTitle {...props} />
    </VBox>
  );
});

export const AgendaItemCard: FC<AgendaItemCardProps> = observer((props) => {
  const { t } = useTranslation();
  const onOpen = useMouseEventHandler(props.onOpen);
  return (
    <Card
      padding={"lg"}
      gap={"lg"}
      rounded={!isMobileView()}
      alignItems={"start"}
      htmlProps={
        onOpen && isMobileView()
          ? {
              onClick: onOpen,
              tabIndex: 0,
              role: "button",
            }
          : undefined
      }
      title={props.title}
      subTitle={<AgendaItemSubTitle {...props} />}
    >
      {when(props.description, (description) => renderMessageOrMarkdown(t, description))}
      {props.children}
      {props.infoTags && props.infoTags.length > 0 && (
        <Box>
          {props.infoTags.map((x) => (
            <InfoTag key={x.id} {...x} />
          ))}
        </Box>
      )}
      {onOpen && props.openVariant === "primary" ? (
        <Button
          variant={"primary"}
          label={props.openLabel ?? { key: "show-more" }}
          arrow={"right"}
          onClick={onOpen}
        />
      ) : onOpen ? (
        <LinkButton
          label={props.openLabel ?? { key: "show-more" }}
          arrow={"right"}
          onClick={onOpen}
        />
      ) : undefined}
    </Card>
  );
});

export const AgendaItemDetails: FC<AgendaItemCardProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <VBox padding={"lg"} gap={"lg"}>
      <Box justifyContent={"space-between"} alignItems={"center"}>
        <LinkButton arrow="left" label={{ key: "back" }} onClick={props.onClose} />
        {props.onEdit && (
          <LinkButton icon={"edit"} label={{ key: "edit" }} onClick={props.onEdit} />
        )}
      </Box>
      <AgendaItemHeader {...props} />
      {when(props.description, (description) => renderMessageOrMarkdown(t, description))}
      {props.children}
      {props.infoTags?.map((x) => (
        <InfoTagDetails key={x.id} {...x} variant={"box"} />
      ))}
      {props.comments?.comments && <CommentsView {...props.comments} />}
    </VBox>
  );
});

export const MeetingLogin = observer(function MeetingLogin({
  authStatus,
  companyLogo,
  companyName,
  meetingPassword,
  onSubmit,
}: {
  authStatus: "PASSWORD_NEEDED" | "WRONG_PASSWORD" | "PASSWORD_BLOCKED";
  companyLogo?: string;
  companyName?: string;
  meetingPassword: StringFieldModel;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  return (
    <VBox bg={isMobileView()} fullscreen>
      <Container maxWidth={"lg"} gap={"xl"} alignItems={"center"}>
        {!isMobileView() && (
          <Box justifyContent={"center"} marginTop={"lg"}>
            <DcompanyLogoImg full className={styles.logo} style={{ width: 350 }} />
          </Box>
        )}
        <Grid
          bg
          padding={isMobileView() ? "sm" : "lg"}
          cols={isMobileView() ? 1 : 2}
          marginTop={"lg"}
          gap={"xl"}
        >
          <GridItem padding={"lg"}>
            <VBox>
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={companyName ?? ""}
                  className={styles.logo}
                  width={isMobileView() ? "100%" : 350}
                />
              ) : (
                <h4>{companyName}</h4>
              )}
              <Markdown value={t("meetings.password-login")} enableLinks />
            </VBox>
          </GridItem>
          <GridItem padding={"lg"}>
            <VBox gap={"lg"}>
              <Box textSize={"lg"} fontWeight={"bold"}>
                {t("meetings.enter-password")}
              </Box>
              {authStatus === "WRONG_PASSWORD" && (
                <Alert message={t("meetings.wrong-password")} type={"error"} />
              )}
              {authStatus === "PASSWORD_BLOCKED" ? (
                <Alert message={t("meetings.password-blocked")} type={"error"} />
              ) : (
                <>
                  <MTextInput
                    model={meetingPassword}
                    type={"password"}
                    autofocus
                    autoComplete={"one-time-code"}
                    onKeyDown={(e) => isEnter(e) && onSubmit()}
                  />
                  <Box justifyContent={"end"}>
                    <Button variant={"primary"} label={t("continue")} onClick={() => onSubmit()} />
                  </Box>
                </>
              )}
            </VBox>
          </GridItem>
        </Grid>
      </Container>
    </VBox>
  );
});
